"use client";

import React from 'react';
import Link from 'next/link';
import { LandingHeader } from '@/app/components/LandingHeader';
import { Footer } from '@/app/components/Footer';

export default function CookiePolicyPage() {
  return (
    <div className="flex flex-col min-h-screen bg-background text-foreground font-body">
      <LandingHeader transparent />
      
      <main className="flex-1">
        <section className="pt-32 pb-20 px-6">
          <div className="container mx-auto max-w-4xl">
            <h1 className="font-display text-4xl md:text-6xl mb-4">Cookie Policy</h1>
            <p className="text-muted-foreground mb-12">Last updated: 19 December 2025</p>
            
            <div className="prose prose-slate max-w-none space-y-8 text-lg font-light leading-relaxed text-muted-foreground">
              <p>
                This Cookie Policy explains how Willdone (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) uses cookies and similar technologies on our website (the &ldquo;Website&rdquo;).
              </p>
              
              <p>
                For more information about how we use personal data generally, please see our <Link href="/privacy-policy" className="text-primary hover:underline underline-offset-4 font-medium">Privacy Policy</Link>.
              </p>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">1. Who we are and how to contact us</h2>
                <p>
                  <strong>Contact email:</strong> hello@willdone.co.uk
                </p>
                <p className="mt-4 italic">
                  (Operator legal details may be shown in a Legal Information section on the Website and or at checkout.)
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">2. What cookies are</h2>
                <p>
                  Cookies are small text files placed on your device when you visit a website. Similar technologies (like pixels, tags, local storage, SDKs, or scripts) can also store or access information on your device.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">3. Why we use cookies</h2>
                <p>We use cookies and similar technologies to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>make the Website work properly</li>
                  <li>keep the Website secure and help prevent fraud</li>
                  <li>remember your preferences (where enabled)</li>
                  <li>understand how the Website is used (analytics), so we can improve it (where enabled)</li>
                  <li>support payments and checkout (via Stripe)</li>
                </ul>
                <p className="mt-4">
                  Under UK rules, you must be told clearly what cookies do and what they&rsquo;re used for.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">4. Types of cookies we use</h2>
                
                <h3 className="text-xl font-medium text-foreground mt-4 mb-2">4.1 Strictly necessary cookies</h3>
                <p>
                  These cookies are required for the Website to function (for example, security, session management, load balancing, and basic features you request). These do not require consent under the cookie rules when they are genuinely essential for what you asked the site to do.
                </p>

                <h3 className="text-xl font-medium text-foreground mt-6 mb-2">4.2 Preference cookies</h3>
                <p>
                  These remember choices you make (for example, language or display preferences). These are not always strictly necessary, so we will only use them if you enable them in our cookie settings.
                </p>

                <h3 className="text-xl font-medium text-foreground mt-6 mb-2">4.3 Analytics cookies</h3>
                <p>
                  These help us understand how visitors use the Website (for example, pages visited, time spent, errors). We will only use analytics cookies if you consent via our cookie banner or settings. Consent must meet the UK GDPR standard (freely given, specific, informed, and signalled by a clear positive action).
                </p>

                <h3 className="text-xl font-medium text-foreground mt-6 mb-2">4.4 Marketing cookies</h3>
                <p>
                  These track browsing activity to show relevant advertising or measure marketing performance. If we use these, we will only set them with your consent.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">5. Third-party cookies and technologies</h2>
                <p>
                  Some features we use may involve third parties placing cookies or accessing information on your device (for example, payment providers, embedded content, analytics tools). These third-party technologies are also covered by the cookie rules.
                </p>
                <p className="mt-4">
                  <strong>Stripe:</strong> If you pay for a service, Stripe may set cookies or use similar technologies to process payments and help prevent fraud.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">6. Your choices and how to manage cookies</h2>
                
                <h3 className="text-xl font-medium text-foreground mt-4 mb-2">6.1 Cookie banner and settings</h3>
                <p>When you first visit our Website, we show a cookie banner or settings tool that lets you:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>accept all non-essential cookies</li>
                  <li>reject non-essential cookies</li>
                  <li>choose which categories to enable</li>
                </ul>
                <p className="mt-4 italic">
                  Non-essential cookies should not be set until you have given consent, and consent must involve an unambiguous positive action.
                </p>

                <h3 className="text-xl font-medium text-foreground mt-6 mb-2">6.2 Browser controls</h3>
                <p>
                  You can also control cookies through your browser settings (for example, blocking cookies or deleting stored cookies). If you block strictly necessary cookies, parts of the Website may not work.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">7. Cookie list (what we actually set)</h2>
                <p>
                  Cookies can change as we update the Website. We recommend listing the cookies you use here (or via your consent tool) including name, purpose, provider, and expiry.
                </p>
                
                <div className="mt-6 space-y-6">
                  <div>
                    <h3 className="text-xl font-medium text-foreground mb-2">Strictly necessary (examples):</h3>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>[cookie name] &ndash; [purpose] &ndash; [expiry] &ndash; Provider: Willdone</li>
                      <li>[cookie name] &ndash; [purpose] &ndash; [expiry] &ndash; Provider: Stripe (if applicable)</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-medium text-foreground mb-2">Analytics (only if enabled):</h3>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>[cookie name] &ndash; [purpose] &ndash; [expiry] &ndash; Provider: [e.g., Google Analytics/other]</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-medium text-foreground mb-2">Preferences (only if enabled):</h3>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>[cookie name] &ndash; [purpose] &ndash; [expiry] &ndash; Provider: Willdone</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-medium text-foreground mb-2">Marketing (only if enabled):</h3>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>[cookie name] &ndash; [purpose] &ndash; [expiry] &ndash; Provider: [provider]</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">8. Changes to this Cookie Policy</h2>
                <p>
                  We may update this policy from time to time. We will post the updated version on the Website and update the effective date.
                </p>
                <p className="mt-4 italic">
                  (ICO guidance notes cookie and privacy guidance may be updated in light of legislative changes, so we periodically review our approach.)
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-display text-foreground mb-4">9. Contact</h2>
                <p>
                  Questions about cookies or your privacy? Email <strong>hello@willdone.co.uk</strong>.
                </p>
              </section>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}



